<template>
  <div class="flex justify-between" v-if="label || tooltip || usePersonalisation || inputMenuData">
    <div class="flex">
      <label class="mb-2 block" v-if="label">
        <template v-if="label">
          {{ label }}
        </template>
      </label>

      <Tooltip placement="right" v-if="tooltip">
        <template #title>
          <p class="p-1 leading-snug">{{ tooltip }}</p>
        </template>
        <InformationSvg class="ml-2 mt-1" />
      </Tooltip>
    </div>

    <template v-if="usePersonalisation && api">
      <PersonalisationToken :api="api" @token-selected="onTokenSelected" />
    </template>

    <template v-if="inputMenuData">
      <InputMenu :dataset="inputMenuData" @entry-selected="onEntrySelected">
        <slot />
      </InputMenu>
    </template>
  </div>

  <input
    v-bind="$attrs"
    :value="modelValue"
    @input="updateModel"
    @focus="$emit('focus')"
    class="appearance-none border rounded w-full py-2 px-3 text-gray-700 bg-lightGrey focus:outline-none"
    ref="inputElement"
    :disabled="isDisabled"
  />

  <template v-if="inputMenuData">
    <div class="flex justify-end">
      <button v-if="selectedEntry && selectedEntry.id" @click="onSaveSelected" class="p-2">
        Save
      </button>
      <button v-if="selectedEntry" @click="onSaveAsSelected" class="p-2">Save As</button>
    </div>
  </template>
</template>

<script lang="ts">
// @ts-ignore
import InformationSvg from '@/mw-components/assets/svg/Information.svg?component';
import { Tooltip } from 'ant-design-vue';
import 'ant-design-vue/lib/tooltip/style/index.css';
import { defineComponent, nextTick, PropType, ref } from 'vue';
import InputMenu from '../InputMenu.vue';
import PersonalisationToken from '../PersonalisationToken.vue';

type Entry = {
  id?: string;
  label: string;
  value: string;
};

export default defineComponent({
  name: 'BrandInput',
  inheritAttrs: false,
  emits: ['saveEntry', 'saveEntryAs', 'update:modelValue', 'focus'],
  props: {
    label: {
      type: String,
      default: '',
    },
    tooltip: {
      type: String,
      default: '',
    },
    usePersonalisation: {
      type: Boolean,
      default: false,
    },
    modelValue: {
      type: [String, Number],
      default: '',
    },
    inputMenuData: {
      type: Array as PropType<Array<Entry>>,
    },
    api: {
      type: Object,
    },
    isDisabled: {
      type: Boolean,
    },
  },

  components: {
    Tooltip,
    InformationSvg,
    PersonalisationToken,
    InputMenu,
  },

  setup(props, { emit }) {
    const inputElement = ref<HTMLInputElement | null>(null);
    const selectedEntry = ref<Entry>();

    const onTokenSelected = (token: string): void => {
      if (!inputElement.value) return;
      const [start, end, value] = [
        inputElement.value.selectionStart,
        inputElement.value.selectionEnd,
        inputElement.value.value,
      ];

      const tokenString = `${value.length === start ? ' ' : ''}${token}`;
      inputElement.value.setRangeText(tokenString, start ? start : 0, end ? end : 0, 'select');
      inputElement.value.focus();

      emit('update:modelValue', inputElement.value.value);
    };

    const onEntrySelected = (entry: Entry) => {
      selectedEntry.value = entry;

      nextTick(() => {
        if (!inputElement.value) return;
        inputElement.value.value = entry.value;
      });

      emit('update:modelValue', entry.value);
    };

    const onSaveSelected = () => {
      if (!selectedEntry.value) return;
      if (!inputElement.value) return;
      emit('saveEntry', {
        ...selectedEntry.value,
        value: inputElement.value.value,
      });
    };

    const onSaveAsSelected = () => {
      if (!inputElement.value) return;
      emit('saveEntryAs', inputElement.value.value);
    };

    const updateModel = (e: Event) => {
      emit('update:modelValue', (e.target as HTMLInputElement).value);
    };

    return {
      inputElement,
      selectedEntry,
      onTokenSelected,
      onEntrySelected,
      onSaveSelected,
      onSaveAsSelected,
      updateModel,
    };
  },
});
</script>
